import { observer } from 'mobx-react-lite'

import { Form, FormField } from '@/Core/UI/Form'
import {
    EntityBody,
    EntityHeader,
    EntityHero,
    EntityLayout,
    FieldCell,
    FieldGrid,
    FieldSection,
    FileFieldCell,
} from '@/Ship/Components/EntityCard'
import { formatDateUk } from '@/Ship/Helpers/fieldViews'

import { CRM_CONTRACT_FIELD_LABELS as L } from './CrmContract'

import type { ReactNode } from 'react'

/** Те, що картка договору бере з ядрового `formContext`. */
export interface CrmContractFormContextLike {
    isEditable?: boolean
    values?: {
        uuid?: unknown
        title?: unknown
        number?: unknown
        date?: unknown
        [key: string]: unknown
    }
}

interface CrudContextLike {
    resource?: { fields?: Record<string, unknown> }
}

interface TemplateProps {
    crudContext: CrudContextLike | undefined
    formContext: CrmContractFormContextLike | undefined
}

/**
 * Блоки картки договору (`SPEC-009` §8.5): сам договір із файлом, далі —
 * службова інформація. Поля, додані в ресурс пізніше, — у «Договорі», щоб не
 * зникали мовчки.
 */
const SECTIONS = {
    contract: ['number', 'date', 'forwarder'],
    service: ['createdAt', 'createdBy', 'updatedAt', 'updatedBy'],
} as const

/** Ставить сервер: `title` — шапка, `id` нікому не потрібен. */
const HIDDEN = new Set<string>(['title', 'id'])

const FILE = 'file'

const PLACED = new Set<string>([...Object.values(SECTIONS).flat(), FILE, ...HIDDEN])

const text = (value: unknown): string => (typeof value === 'string' ? value.trim() : '')

/**
 * Шапка договору — «№ <номер> від <дд.мм.рррр>», як `title` запису. Окремий
 * observer: номер і дату правлять, а шаблон значень не читає (`SPEC-002` §4.2).
 */
const CrmContractCardHeader = observer(({ formContext }: { formContext: CrmContractFormContextLike }) => {
    const values = formContext.values ?? {}
    const number = text(values.number)
    const date = values.date ? formatDateUk(values.date) : ''
    const title = number ? `№ ${number}${date && date !== '—' ? ` від ${date}` : ''}` : text(values.title)

    return <EntityHeader {...(title !== '' ? { title } : {})} />
})

/**
 * Картка договору клієнта чи перевізника (`SPEC-008` §7.1, `SPEC-009` §8.5):
 * «Номер договору»*, «Дата договору»*, «Експедитор», «Файл»; «Службова
 * інформація».
 *
 * Файл — двокроковий (`SPEC-008` §5.7.1): у формі — ядровий `FileInput`, який
 * вантажить файл одразу, а запис прикріплює його при збереженні
 * (`attachUploadedFiles` ресурсу). Тож файл можна додати вже у формі
 * створення — у легасі лише після збереження (D-40). У перегляді — посилання,
 * що відкривається в новій вкладці (`FileFieldCell`).
 *
 * Шаблон один на обох власників. Поля власника (`client`, `carrier`) у
 * ресурсі немає: його задає вкладка при створенні (`initialValues`), і змінити
 * його не можна (`SPEC-008` §5.1).
 */
export const CrmContractCardTemplate = observer(({ crudContext, formContext }: TemplateProps) => {
    const fields = crudContext?.resource?.fields ?? {}
    const uuid = formContext?.values?.uuid
    const isNew = typeof uuid !== 'string' || uuid === ''

    // Поля, додані в ресурс пізніше, не мають зникати мовчки
    const rest = Object.keys(fields).filter((name) => !PLACED.has(name))

    const cell = (name: string): ReactNode =>
        name in fields ? (
            <FieldCell key={name}>
                <FormField name={name} />
            </FieldCell>
        ) : null

    const grid = (names: readonly string[], file = false) => (
        <Form formContext={formContext}>
            <FieldGrid>
                {names.map(cell)}
                {file && FILE in fields ? (
                    <FieldCell key={FILE} wide>
                        <FileFieldCell formContext={formContext} name={FILE} label={L.file} />
                    </FieldCell>
                ) : null}
            </FieldGrid>
        </Form>
    )

    const main = (
        <>
            <FieldSection title="Договір">{grid([...SECTIONS.contract, ...rest], true)}</FieldSection>
            <FieldSection title="Службова інформація" hidden={isNew}>
                {grid(SECTIONS.service)}
            </FieldSection>
        </>
    )

    return (
        <EntityLayout>
            {!isNew && formContext ? (
                <EntityHero>
                    <CrmContractCardHeader formContext={formContext} />
                </EntityHero>
            ) : null}
            <EntityBody main={main} />
        </EntityLayout>
    )
})
